import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { signupSchema } from "@eventflow/shared";

import AuthLayout from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/auth/AuthContext";
import { safeNext } from "@/auth/loginUrl";
import { errorCode, errorMessage } from "@/lib/errors";

export default function SignUp() {
  const { signup, status } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [form, setForm] = useState({ full_name: "", email: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  if (status === "authed") return <Navigate to={next} replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const parsed = signupSchema.safeParse({ full_name: form.full_name, email: form.email, password: form.password });
    if (!parsed.success) {
      const errs = {};
      for (const issue of parsed.error.issues) errs[issue.path[0]] = issue.message;
      setFieldErrors(errs);
      return;
    }
    if (form.password !== form.confirm) {
      setFieldErrors({ confirm: "Passwords do not match." });
      return;
    }
    setFieldErrors({});
    setBusy(true);
    try {
      await signup(parsed.data);
      navigate(`/VerifyEmail?sent=1&next=${encodeURIComponent(next)}`, { replace: true });
    } catch (err) {
      if (errorCode(err) === "PASSWORD_SETUP_REQUIRED") {
        setError("An account with that email already exists from our previous system. We emailed you a link to set your password.");
      } else if (errorCode(err) === "EMAIL_TAKEN") {
        setError("An account with that email already exists. Try signing in instead.");
      } else {
        setError(errorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle="It only takes a moment"
      footer={
        <>
          Already have an account?{" "}
          <Link to={`/Login?next=${encodeURIComponent(next)}`} className="text-indigo-600 font-medium hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
        <div className="space-y-1.5">
          <Label htmlFor="full_name">Full Name</Label>
          <Input id="full_name" autoComplete="name" value={form.full_name} onChange={set("full_name")} required autoFocus />
          {fieldErrors.full_name && <p className="text-xs text-red-600">{fieldErrors.full_name}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" value={form.email} onChange={set("email")} required />
          {fieldErrors.email && <p className="text-xs text-red-600">{fieldErrors.email}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" autoComplete="new-password" value={form.password} onChange={set("password")} required placeholder="At least 8 characters" />
          {fieldErrors.password && <p className="text-xs text-red-600">{fieldErrors.password}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm">Confirm Password</Label>
          <Input id="confirm" type="password" autoComplete="new-password" value={form.confirm} onChange={set("confirm")} required />
          {fieldErrors.confirm && <p className="text-xs text-red-600">{fieldErrors.confirm}</p>}
        </div>
        <Button type="submit" disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-700">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
        </Button>
      </form>
    </AuthLayout>
  );
}
